/**
 * may12CajaDemoSeed.data.ts — Dataset estático demo (ancla 12 may 2026) para caja.
 *
 * QUÉ HACE: Define ventas, movimientos, creditos y un cierre cerrado coherentes
 * con los totales del escenario real de prueba (multicaja pc1, Juan/Carlos).
 * Las fechas usan el prefijo `2026-05-12`; el bootstrap de la demo las proyecta al día local.
 *
 * QUÉ NO DEBE HACER: Importar React ni tocar localStorage (eso lo hace apply).
 *
 * Reglas: IDs estables (`may12-*`) para que el seed sea idempotente por versión.
 */

import type {
  CierreCaja,
  Credito,
  MovimientoCaja,
  Venta,
} from "../types/caja.types";
import { MOTIVO_RETIRO_TOPE_EFECTIVO } from "../constants/cajaMovimientoMotivos";
import { enrichMay12VentasWithPropina } from "./enrichMay12VentasWithPropina";
import {
  calcBalanceCajaFisicaSesion,
  calcResumenVentasSesion,
} from "../utils/cajaResumenVentasSesion";
import { resolveDemoCatalogLine } from "./demoInventoryProductMap";
import { buildCierreAuditMetrics } from "../utils/cierreAuditMetrics";

/** Sube la versión al cambiar ventas/movs del preset (fuerza re-hidratación en LS). */
export const MAY12_DEMO_SEED_VERSION = "2026-05-12-may12-v28";

export const MAY12_PUNTO_ID = "pc1";
/** Sesión cerrada del turno mañana (histórico + snapshot del cierre). */
export const MAY12_SESION_MAIN_ID = "sesion_may12_demo_main";
export const MAY12_SESION_TARDE_ID = "sesion_may12_demo_tarde";
export const MAY12_SESION_NOCHE_ID = "sesion_may12_demo_noche";
export const DEMO_CIERRE_TARDE_ID = "1778595754642";
export const DEMO_CIERRE_NOCHE_ID = "1778595754643";
/** Cierre del turno madrugada (pc1) — simula un negocio con ventas 24h. */
export const DEMO_CIERRE_MADRUGADA_ID = "1778595754644";
/** Alias histórico — la sesión activa demo usa `MAY12_SESION_MAIN_ID`. */
export const MAY12_SESION_OPEN_ID = MAY12_SESION_MAIN_ID;

const JP = { id: "emp_1", nombre: "Juan Pérez" as const };
const CR = { id: "emp_3", nombre: "Carlos Ruiz" as const };
const DT = { id: "emp_4", nombre: "Dylan Torres" as const };
const EG = { id: "emp_5", nombre: "Esteban Gómez" as const };
const SC = { id: "emp_6", nombre: "Sofía Castro" as const };
const YA = { id: "emp_2", nombre: "Yinet Alarcón" as const };
const JD = { id: "emp_7", nombre: "José Delgado" as const };
const MB = { id: "emp_9", nombre: "Miriam Beltrán" as const };
const LM = { id: "emp_10", nombre: "Luz Marina" as const };
const DP = { id: "emp_11", nombre: "Diana Pinzón" as const };

const line = (
  pid: string,
  nombre: string,
  cant: number,
  pUnit: number,
  puntoCajaId: string = MAY12_PUNTO_ID,
): Venta["productos"][0] => {
  const resolved = resolveDemoCatalogLine(pid, nombre, puntoCajaId);
  return {
    productoId: resolved.productoId,
    nombre: resolved.nombre,
    cantidad: cant,
    precioUnitario: pUnit,
  };
};

/** Reparte un total mixto con la misma proporción del mega-ticket original. */
const mixtoBreakdownFromTotal = (total: number): Venta["detalleMetodoPago"] => {
  const base = 3_134_985;
  const parts = [
    {
      method: "efectivo" as const,
      amount: Math.round((2_000_000 * total) / base),
    },
    { method: "nequi" as const, amount: Math.round((500_000 * total) / base) },
    {
      method: "daviplata" as const,
      amount: Math.round((334_985 * total) / base),
    },
  ];
  const allocated = parts.reduce((s, p) => s + p.amount, 0);
  return {
    breakdown: [
      ...parts,
      { method: "tarjeta", amount: Math.max(0, total - allocated) },
    ],
  };
};

const ventaBase = (
  partial: Omit<Venta, "sesionId" | "puntoCajaId"> & {
    puntoCajaNombre?: string;
  },
): Venta => ({
  sesionId: MAY12_SESION_MAIN_ID,
  puntoCajaId: MAY12_PUNTO_ID,
  puntoCajaNombre: "Caja principal",
  ...partial,
});

/** Ventas del turno cerrado (incluye anulada en historial; el cierre filtra en apply). */
const MAY12_VENTAS_MAIN_RAW: Venta[] = [
  ventaBase({
    id: "may12-v-01",
    fecha: "2026-05-12T08:02:00.000-05:00",
    total: 948_000,
    metodoPago: "mixto",
    detalleMetodoPago: mixtoBreakdownFromTotal(948_000),
    productos: [
      line("m12-p1", "Gafas de Sol Unisex", 1, 449_000),
      line("m12-p2", "Reloj Deportivo Hombre", 1, 499_000),
    ],
    usuarioId: JP.id,
    userName: JP.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-13",
    fecha: "2026-05-12T08:02:30.000-05:00",
    total: 823_000,
    metodoPago: "mixto",
    detalleMetodoPago: mixtoBreakdownFromTotal(823_000),
    productos: [
      line("m12-p5", "Batería Auto 12V 70Ah", 1, 589_000),
      line("m12-p6", "Gato Hidráulico 2T", 1, 129_000),
      line("m12-p7", "Bombillo Halógeno H4", 1, 105_000),
    ],
    usuarioId: DT.id,
    userName: DT.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-14",
    fecha: "2026-05-12T08:03:00.000-05:00",
    total: 658_000,
    metodoPago: "mixto",
    detalleMetodoPago: mixtoBreakdownFromTotal(658_000),
    productos: [
      line("m12-p11", "Perfume Hombre CK", 1, 349_000),
      line("m12-p12", "Tenis Adidas Originals Stan Smith", 1, 309_000),
    ],
    usuarioId: EG.id,
    userName: EG.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-15",
    fecha: "2026-05-12T08:03:30.000-05:00",
    total: 706_985,
    metodoPago: "mixto",
    detalleMetodoPago: mixtoBreakdownFromTotal(706_985),
    productos: [
      line("m12-p3", "Cloro Clorox 1L", 1, 6_900),
      line("m12-p4", "Papel Higiénico Familia x12", 1, 27_500),
      line("m12-p8", "Agua Cristal Botella 600ml", 1, 4_300),
      line("m12-p9", "Base Líquida Fenty Beauty", 1, 189_000),
      line("m12-p10", "Esmalte Uñas OPI", 1, 50_000),
      line("m12-p13", "Tacón Mujer Cerrado 8cm", 1, 198_000),
      line("m12-p14", "Costilla de Cerdo 1kg", 1, 81_000),
      line("m12-p15", "Servicios y otros (combo)", 1, 149_285),
    ],
    usuarioId: DP.id,
    userName: DP.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-02",
    fecha: "2026-05-12T08:08:00.000-05:00",
    total: 140_070,
    metodoPago: "efectivo",
    productos: [
      line("m12-h1", "Detergente Ariel 2kg", 1, 29_000),
      line("m12-h2", "Cloro Clorox 1L", 1, 6_900),
      line("m12-h3", "Papel Higiénico Familia x12", 1, 27_500),
      line("m12-h4", "Bolsas de Basura 80L x20", 1, 10_500),
      line("m12-h5", "Shampoo Head & Shoulders 700ml", 1, 24_500),
      line("m12-h6", "Trapeador Microfibra", 1, 35_000),
      line("m12-h7", "Insumos adicionales", 1, 6_670),
    ],
    usuarioId: DT.id,
    userName: DT.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-03",
    fecha: "2026-05-12T08:09:00.000-05:00",
    total: 240_400,
    metodoPago: "tarjeta",
    productos: [
      line("m12-med1", "Tensiómetro Digital Brazo", 1, 189_000),
      line("m12-med2", "Termómetro Digital", 1, 35_000),
      line("m12-med3", "Ibuprofeno 400mg x12", 1, 7_500),
      line("m12-med4", "Alcohol Antiséptico 70% 500ml", 1, 8_900),
      line("m12-med5", "Suministros farmacia", 1, 1_000),
    ],
    usuarioId: EG.id,
    userName: EG.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-04",
    fecha: "2026-05-12T08:10:00.000-05:00",
    total: 107_400,
    metodoPago: "nequi",
    productos: [
      line("m12-s1", "Doritos Nacho 140g", 1, 7_500),
      line("m12-s2", "Cheetos Boliqueso 90g", 1, 5_900),
      line("m12-s3", "De Todito Mix 150g", 1, 8_500),
      line("m12-s4", "Trident Menta x12", 1, 24_500),
      line("m12-s5", "Bon Bon Bum Fresa x24", 1, 14_000),
      line("m12-s6", "Galleta Festival Chocolate x24", 1, 20_000),
      line("m12-s7", "Chocolatina Jet Display x24", 1, 27_000),
    ],
    usuarioId: SC.id,
    userName: SC.nombre,
    estado: "devolucion-parcial",
    devoluciones: [
      {
        id: "DEV-1778591863389",
        ventaId: "may12-v-04",
        fecha: "2026-05-12T08:17:00.000-05:00",
        items: [
          {
            productoId: "sku-m12-4a",
            nombre: "Producto devuelto (demo)",
            cantidad: 1,
            precioUnitario: 13_400,
          },
        ],
        montoReembolsado: 13_400,
        metodoPago: "efectivo",
        motivoDevolucion: "producto vencido devolucion de dinero",
        usuarioId: JP.id,
        userName: JP.nombre,
      },
    ],
  }),
  ventaBase({
    id: "may12-v-05",
    fecha: "2026-05-12T08:10:00.000-05:00",
    total: 628_000,
    metodoPago: "daviplata",
    productos: [
      line("m12-a1", "Mochila Urbana 25L — Color: Negro", 1, 450_000),
      line("m12-a2", "Kit escolar complemento", 1, 178_000),
    ],
    usuarioId: JP.id,
    userName: JP.nombre,
    estado: "anulada",
    anuladaEn: "2026-05-12T08:15:00.000-05:00",
    anuladaPor: "Juan Pérez",
    motivoAnulacion: "venta erronea no esiste dinero esta probando la caja",
  }),
  ventaBase({
    id: "may12-v-06",
    fecha: "2026-05-12T08:24:00.000-05:00",
    total: 449_000,
    metodoPago: "efectivo",
    productos: [
      line("sku-m12-6", "Gafas de Sol Unisex — Color: Negro", 1, 449_000),
    ],
    usuarioId: YA.id,
    userName: YA.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-07",
    fecha: "2026-05-12T08:23:00.000-05:00",
    total: 129_500,
    metodoPago: "mixto",
    detalleMetodoPago: {
      breakdown: [
        { method: "efectivo", amount: 80_000 },
        { method: "nequi", amount: 49_500 },
      ],
    },
    productos: [
      line("m12-f1", "Hot Dog Sencillo", 2, 9_500),
      line("m12-f2", "Perro Mixto Especial", 1, 13_500),
      line("m12-f3", "Sándwich de Pollo", 1, 15_500),
      line("m12-f4", "Hamburguesa Clásica", 1, 18_000),
      line("m12-f5", "Alitas BBQ x6", 1, 22_000),
      line("m12-f6", "Arepa de Carne", 1, 11_500),
      line("m12-f7", "Arepa de Huevo", 1, 5_500),
      line("m12-f8", "Hamburguesa Doble Bacon", 1, 24_500),
    ],
    usuarioId: JD.id,
    userName: JD.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-08",
    fecha: "2026-05-12T08:25:00.000-05:00",
    total: 606_050,
    metodoPago: "tarjeta",
    productos: [
      line("m12-fl1", "Canasta Frutal y Flores", 1, 149_000),
      line("m12-fl2", "Arreglo Condolencias", 1, 189_000),
      line("m12-fl3", "Caja de Rosas Preservadas + servicio", 1, 268_050),
    ],
    usuarioId: MB.id,
    userName: MB.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-09",
    fecha: "2026-05-12T08:26:00.000-05:00",
    total: 948_000,
    metodoPago: "nequi",
    productos: [
      line("m12-ac1", "Gafas de Sol Unisex", 1, 449_000),
      line("m12-ac2", "Reloj Deportivo Hombre", 1, 499_000),
    ],
    usuarioId: CR.id,
    userName: CR.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-16",
    fecha: "2026-05-12T08:26:30.000-05:00",
    total: 412_700,
    metodoPago: "nequi",
    productos: [
      line("m12-ac3", "Bolso Dama Cuero", 1, 289_000),
      line("m12-ac4", "Complementos moda", 1, 123_700),
    ],
    usuarioId: CR.id,
    userName: CR.nombre,
    estado: "activa",
  }),
  ventaBase({
    id: "may12-v-10",
    fecha: "2026-05-12T08:27:00.000-05:00",
    total: 129_000,
    metodoPago: "daviplata",
    productos: [
      line("sku-m12-10", "Mochila Urbana 25L — Color: Negro", 1, 129_000),
    ],
    usuarioId: LM.id,
    userName: LM.nombre,
    estado: "activa",
  }),
];

export const MAY12_VENTAS_MAIN = enrichMay12VentasWithPropina(
  MAY12_VENTAS_MAIN_RAW,
);

/** Venta tarde del mismo turno demo (misma sesión que el resto). */
const MAY12_VENTA_POST_CIERRE_RAW: Venta = {
  id: "may12-v-11",
  fecha: "2026-05-12T12:38:00.000-05:00",
  total: 449_000,
  metodoPago: "mixto",
  detalleMetodoPago: {
    breakdown: [
      { method: "efectivo", amount: 200_000 },
      { method: "nequi", amount: 249_000 },
    ],
  },
  productos: [
    line("sku-m12-11", "Gafas de Sol Unisex — Color: Negro", 1, 449_000),
  ],
  usuarioId: JP.id,
  userName: JP.nombre,
  sesionId: MAY12_SESION_MAIN_ID,
  puntoCajaId: MAY12_PUNTO_ID,
  estado: "activa",
};

export const MAY12_VENTA_POST_CIERRE = enrichMay12VentasWithPropina([
  MAY12_VENTA_POST_CIERRE_RAW,
])[0];

export const MAY12_MOVIMIENTOS: MovimientoCaja[] = [
  {
    id: "may12-m-15",
    tipo: "ingreso",
    monto: 31_188,
    motivo: "Abono a crédito: maria",
    fecha: "2026-05-12T09:03:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "daviplata",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: "Caja principal",
    estado: "activo",
  },
  {
    id: "may12-m-14",
    tipo: "ingreso",
    monto: 62_375,
    motivo: "Abono a crédito: maria",
    fecha: "2026-05-12T09:01:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "nequi",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-13",
    tipo: "ingreso",
    monto: 124_750,
    motivo: "Abono a crédito: maria",
    fecha: "2026-05-12T09:00:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "tarjeta",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-12",
    tipo: "ingreso",
    monto: 249_500,
    motivo: "Abono a crédito: maria",
    fecha: "2026-05-12T08:59:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "efectivo",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-11",
    tipo: "ingreso",
    monto: 40_000,
    motivo: "Cliente debía — pago debia cliente",
    fecha: "2026-05-12T08:44:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "tarjeta",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-10",
    tipo: "ingreso",
    monto: 20_000,
    motivo: "Aporte — juan debia al jefe",
    fecha: "2026-05-12T08:43:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "daviplata",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-09",
    tipo: "ingreso",
    monto: 50_000,
    motivo: "Pago de saldo — cliente debia",
    fecha: "2026-05-12T08:42:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "nequi",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-08",
    tipo: "ingreso",
    monto: 40_000,
    motivo: "Pago de saldo — pago del prestamo trsporte",
    fecha: "2026-05-12T08:34:00.000-05:00",
    usuarioId: CR.id,
    userName: CR.nombre,
    metodoPago: "efectivo",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-07",
    tipo: "gasto",
    monto: 40_000,
    motivo:
      "Transporte — los pago hoy necesito, necesito trasporte para mi esposa",
    fecha: "2026-05-12T08:33:00.000-05:00",
    usuarioId: CR.id,
    userName: CR.nombre,
    tipoSoporte: "con_factura",
    authorizationType: "propia",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-06",
    tipo: "gasto",
    monto: 60_000,
    motivo: "Pago a proveedor — pago de camisetas",
    fecha: "2026-05-12T08:32:00.000-05:00",
    usuarioId: CR.id,
    userName: CR.nombre,
    tipoSoporte: "no_verificado",
    authorizationType: "propia",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-05",
    tipo: "devolucion",
    monto: 13_400,
    motivo:
      "Devolución #DEV-1778591863389: producto vencido devolucion de dinero",
    fecha: "2026-05-12T08:17:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "efectivo",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-04",
    tipo: "anulacion",
    monto: 628_000,
    motivo:
      "Anulación de venta T-65798736: venta erronea no esiste dinero esta probando la caja",
    fecha: "2026-05-12T08:15:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "daviplata",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-03",
    tipo: "ingreso",
    monto: 30_000,
    motivo: "Cliente debía — pago pendiente de cliente",
    fecha: "2026-05-12T08:06:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    metodoPago: "efectivo",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-02",
    tipo: "gasto",
    monto: 2_000,
    motivo: "Compra de insumos — compra del pan",
    fecha: "2026-05-12T08:05:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    tipoSoporte: "no_verificado",
    authorizationType: "propia",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
  {
    id: "may12-m-01",
    tipo: "retiro",
    monto: 50_000,
    motivo: MOTIVO_RETIRO_TOPE_EFECTIVO,
    fecha: "2026-05-12T08:04:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    tipoSoporte: "con_factura",
    authorizationType: "propia",
    sesionId: MAY12_SESION_MAIN_ID,
    puntoCajaId: MAY12_PUNTO_ID,
    estado: "activo",
  },
];

const denom: Record<number, number> = {
  100_000: 10,
  50_000: 2,
  20_000: 1,
  10_000: 1,
  5_000: 20,
  2_000: 19,
  1_000: 21,
  500: 12,
  200: 123,
  100: 34,
  50: 30,
};

export const MAY12_CREDITOS: Credito[] = [
  {
    id: "may12-f-juan",
    clienteId: "cli-m12-juan",
    clienteNombre: "juan",
    productos: [
      line("sku-fi-1", "Billetera Hombre Cuero — Color: Beige", 1, 100_000),
    ],
    total: 100_000,
    abonado: 0,
    saldoPendiente: 100_000,
    estado: "pendiente",
    fecha: "2026-05-12T08:12:00.000-05:00",
    usuarioId: JP.id,
    userName: JP.nombre,
    nota: "paga el viernes",
    abonos: [],
    sesionId: MAY12_SESION_MAIN_ID,
  },
  {
    id: "may12-f-maria",
    clienteId: "cli-m12-maria",
    clienteNombre: "maria",
    productos: [
      line("sku-fi-2", "Reloj Deportivo Hombre — Color: Beige", 1, 499_000),
    ],
    total: 499_000,
    abonado: 467_813,
    saldoPendiente: 31_187,
    estado: "parcial",
    fecha: "2026-05-12T08:30:00.000-05:00",
    usuarioId: CR.id,
    userName: CR.nombre,
    nota: "paga el domingo",
    abonos: [
      {
        id: "may12-ab-1",
        monto: 249_500,
        metodoPago: "efectivo",
        fecha: "2026-05-12T08:59:00.000-05:00",
        usuarioId: JP.id,
        userName: JP.nombre,
      },
      {
        id: "may12-ab-2",
        monto: 124_750,
        metodoPago: "tarjeta",
        fecha: "2026-05-12T09:00:00.000-05:00",
        usuarioId: JP.id,
        userName: JP.nombre,
      },
      {
        id: "may12-ab-3",
        monto: 62_375,
        metodoPago: "nequi",
        fecha: "2026-05-12T09:01:00.000-05:00",
        usuarioId: JP.id,
        userName: JP.nombre,
      },
      {
        id: "may12-ab-4",
        monto: 31_188,
        metodoPago: "daviplata",
        fecha: "2026-05-12T09:03:00.000-05:00",
        usuarioId: JP.id,
        userName: JP.nombre,
      },
    ],
    sesionId: MAY12_SESION_MAIN_ID,
  },
];

/** Ventas activas del turno demo (sin anuladas; incluye la venta tarde #11). */
export function may12VentasActivasSesion(): Venta[] {
  return [...MAY12_VENTAS_MAIN, MAY12_VENTA_POST_CIERRE].filter(
    (v) => v.estado !== "anulada",
  );
}

/**
 * Snapshot de cierre de referencia (9 ventas hasta ~09:22, sin #11).
 * No se persiste en apply: evita duplicar un cierre «cerrado» mientras la sesión sigue abierta.
 */
const denomIntento3Fallido: Record<number, number> = {
  100_000: 9,
  50_000: 3,
  20_000: 2,
  10_000: 5,
  5_000: 15,
  2_000: 20,
  1_000: 15,
  500: 12,
  200: 100,
  100: 30,
  50: 20,
};

const MAY12_CIERRE_CIEGO_AUDIT: CierreCaja["cierreCiegoAudit"] = {
  intentosFallidos: 3,
  montosDeclarados: [980_000, 1_150_000, 1_280_000],
  desbloqueadoPorLimite: true,
  intentos: [
    {
      numero: 1,
      total: 980_000,
      registradoEn: "2026-05-12T09:18:00.000-05:00",
    },
    {
      numero: 2,
      total: 1_150_000,
      registradoEn: "2026-05-12T09:20:00.000-05:00",
    },
    {
      numero: 3,
      total: 1_280_000,
      registradoEn: "2026-05-12T09:21:00.000-05:00",
      conteoEfectivo: {
        denominaciones: denomIntento3Fallido,
        total: 1_280_000,
      },
    },
  ],
  ultimoIntentoFallidoConteo: {
    denominaciones: denomIntento3Fallido,
    total: 1_280_000,
  },
};

const ventaBaseSesion = (
  sesionId: string,
  partial: Omit<Venta, "sesionId" | "puntoCajaId">,
): Venta => ({
  sesionId,
  puntoCajaId: MAY12_PUNTO_ID,
  puntoCajaNombre: "Caja principal",
  ...partial,
});

export function buildMay12CierreCaja(): CierreCaja {
  const ventSnap = MAY12_VENTAS_MAIN.filter((v) => v.estado !== "anulada");
  const ventasParaCalculo = [...MAY12_VENTAS_MAIN];
  const movSnap = [...MAY12_MOVIMIENTOS].sort(
    (a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime(),
  );
  const creditosSnap = MAY12_CREDITOS.map((f) => ({ ...f }));
  const resumen = calcResumenVentasSesion(
    ventasParaCalculo,
    MAY12_SESION_MAIN_ID,
  );
  const balance = calcBalanceCajaFisicaSesion(
    30_000,
    ventasParaCalculo,
    MAY12_SESION_MAIN_ID,
    MAY12_MOVIMIENTOS,
  );
  const montoCierre = 1_324_500;
  const draftCierre: CierreCaja = {
    id: "1778595754641",
    abiertaEn: "2026-05-12T07:55:00.000-05:00",
    cerradaEn: "2026-05-12T09:22:00.000-05:00",
    montoInicial: 30_000,
    montoCierre,
    diferencia: 0,
    userId: JP.id,
    estado: "cerrada",
    movimientosSnapshot: movSnap,
  };
  const audit = buildCierreAuditMetrics(draftCierre, ventSnap);
  const montoEsperado = audit.esperadoGaveta;
  const diferencia = montoCierre - montoEsperado;

  return {
    id: "1778595754641",
    abiertaEn: "2026-05-12T07:55:00.000-05:00",
    cerradaEn: "2026-05-12T09:22:00.000-05:00",
    montoInicial: 30_000,
    montoCierre,
    diferencia,
    userId: JP.id,
    estado: "cerrada",
    openedByName: "Juan Pérez",
    closedByName: "Juan Pérez",
    label: "Turno mañana",
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: "Caja principal",
    sesionId: MAY12_SESION_MAIN_ID,
    ventasCount: ventSnap.length,
    ventasSnapshot: ventSnap.map((v) => ({ ...v })),
    movimientosSnapshot: movSnap.map((m) => ({ ...m })),
    creditosSnapshot: creditosSnap,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: balance.totalRetiros,
    totalIngresos: balance.totalIngresos,
    totalIngresosEfectivo: balance.totalIngresosEfectivo,
    totalIngresosDigital: balance.totalIngresosDigital,
    motivoDescuadre: "otros",
    notaDescuadre:
      "billete falso encontrado / Auditoría e inyección de estrés sobre sistema.",
    conteoEfectivo: {
      denominaciones: denom,
      total: montoCierre,
    },
    cierreCiegoAudit: MAY12_CIERRE_CIEGO_AUDIT,
  };
}

/** Turno tarde — cierre cuadrado con sobrante menor (demo historial jornada). */
export function buildMay12CierreTarde(): CierreCaja {
  const ventSnap: Venta[] = [
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t1",
      fecha: "2026-05-12T14:12:00.000-05:00",
      total: 385_000,
      metodoPago: "efectivo",
      productos: [
        line("t1", "Camisa Oxford Hombre", 1, 185_000),
        line("t2", "Corbata seda", 1, 200_000),
      ],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t2",
      fecha: "2026-05-12T15:05:00.000-05:00",
      total: 512_400,
      metodoPago: "tarjeta",
      productos: [line("t3", "Set herramientas 120 pzas", 1, 512_400)],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t3",
      fecha: "2026-05-12T16:20:00.000-05:00",
      total: 278_900,
      metodoPago: "mixto",
      detalleMetodoPago: {
        breakdown: [
          { method: "efectivo", amount: 150_000 },
          { method: "nequi", amount: 128_900 },
        ],
      },
      productos: [line("t4", "Zapatillas running", 1, 278_900)],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t4",
      fecha: "2026-05-12T17:10:00.000-05:00",
      total: 94_500,
      metodoPago: "efectivo",
      productos: [line("t5", "Combo café + pastel x3", 3, 31_500)],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t5",
      fecha: "2026-05-12T18:05:00.000-05:00",
      total: 867_200,
      metodoPago: "daviplata",
      productos: [line("t6", "Televisor 43'' Smart", 1, 867_200)],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_TARDE_ID, {
      id: "may12-v-t6",
      fecha: "2026-05-12T18:25:00.000-05:00",
      total: 186_000,
      metodoPago: "nequi",
      productos: [line("t7", "Mochila escolar premium", 1, 186_000)],
      usuarioId: CR.id,
      userName: CR.nombre,
      estado: "activa",
    }),
  ];
  const movSnap: MovimientoCaja[] = [
    {
      id: "may12-m-t1",
      tipo: "ingreso",
      monto: 50_000,
      motivo: "Fondo adicional turno tarde",
      fecha: "2026-05-12T14:08:00.000-05:00",
      usuarioId: CR.id,
      userName: CR.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_TARDE_ID,
    },
    {
      id: "may12-m-t2",
      tipo: "gasto",
      monto: 18_500,
      motivo: "Compra insumos limpieza",
      fecha: "2026-05-12T15:40:00.000-05:00",
      usuarioId: CR.id,
      userName: CR.nombre,
      sesionId: MAY12_SESION_TARDE_ID,
    },
    {
      id: "may12-m-t3",
      tipo: "retiro",
      monto: 120_000,
      motivo: "Pago domiciliario proveedor",
      fecha: "2026-05-12T17:55:00.000-05:00",
      usuarioId: CR.id,
      userName: CR.nombre,
      sesionId: MAY12_SESION_TARDE_ID,
    },
  ];
  const montoInicial = 145_000;
  const resumen = calcResumenVentasSesion(ventSnap, MAY12_SESION_TARDE_ID);
  const balance = calcBalanceCajaFisicaSesion(
    montoInicial,
    ventSnap,
    MAY12_SESION_TARDE_ID,
    movSnap,
  );
  const montoEsperado = balance.montoEsperado;
  const montoCierre = montoEsperado + 12_500;

  return {
    id: DEMO_CIERRE_TARDE_ID,
    abiertaEn: "2026-05-12T14:08:00.000-05:00",
    cerradaEn: "2026-05-12T18:35:00.000-05:00",
    montoInicial,
    montoCierre,
    diferencia: montoCierre - montoEsperado,
    userId: CR.id,
    estado: "cerrada",
    openedByName: CR.nombre,
    closedByName: CR.nombre,
    label: "Turno tarde",
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: "Caja principal",
    sesionId: MAY12_SESION_TARDE_ID,
    ventasCount: ventSnap.length,
    ventasSnapshot: ventSnap,
    movimientosSnapshot: movSnap,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: 120_000,
    totalIngresos: 50_000,
    totalIngresosEfectivo: 50_000,
    totalIngresosDigital: 0,
    motivoDescuadre: "sobrante_desconocido",
    notaDescuadre: "Sobrante menor al contar billetes de $50.000.",
  };
}

/** Turno noche — cierre cuadrado (demo historial jornada). */
export function buildMay12CierreNoche(): CierreCaja {
  const ventSnap: Venta[] = [
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n1",
      fecha: "2026-05-12T19:25:00.000-05:00",
      total: 156_800,
      metodoPago: "efectivo",
      productos: [line("n1", "Cerveza sixpack x2", 2, 78_400)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n2",
      fecha: "2026-05-12T20:10:00.000-05:00",
      total: 423_500,
      metodoPago: "nequi",
      productos: [line("n2", "Audífonos Bluetooth Pro", 1, 423_500)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n3",
      fecha: "2026-05-12T21:05:00.000-05:00",
      total: 89_200,
      metodoPago: "efectivo",
      productos: [line("n3", "Snacks nocturnos combo", 1, 89_200)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n4",
      fecha: "2026-05-12T21:50:00.000-05:00",
      total: 612_000,
      metodoPago: "tarjeta",
      productos: [line("n4", "Tablet 10'' 64GB", 1, 612_000)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n5",
      fecha: "2026-05-12T22:15:00.000-05:00",
      total: 245_600,
      metodoPago: "mixto",
      detalleMetodoPago: {
        breakdown: [
          { method: "efectivo", amount: 145_600 },
          { method: "daviplata", amount: 100_000 },
        ],
      },
      productos: [line("n5", "Kit skincare nocturno", 1, 245_600)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
    ventaBaseSesion(MAY12_SESION_NOCHE_ID, {
      id: "may12-v-n6",
      fecha: "2026-05-12T22:28:00.000-05:00",
      total: 67_400,
      metodoPago: "daviplata",
      productos: [line("n6", "Agua + energética x4", 4, 16_850)],
      usuarioId: SC.id,
      userName: SC.nombre,
      estado: "activa",
    }),
  ];
  const movSnap: MovimientoCaja[] = [
    {
      id: "may12-m-n1",
      tipo: "ingreso",
      monto: 30_000,
      motivo: "Cambio recibido de banco",
      fecha: "2026-05-12T19:12:00.000-05:00",
      usuarioId: SC.id,
      userName: SC.nombre,
      metodoPago: "efectivo",
      sesionId: MAY12_SESION_NOCHE_ID,
    },
    {
      id: "may12-m-n2",
      tipo: "gasto",
      monto: 12_000,
      motivo: "Pago mensajería nocturna",
      fecha: "2026-05-12T21:30:00.000-05:00",
      usuarioId: SC.id,
      userName: SC.nombre,
      sesionId: MAY12_SESION_NOCHE_ID,
    },
  ];
  const montoInicial = 50_000;
  const resumen = calcResumenVentasSesion(ventSnap, MAY12_SESION_NOCHE_ID);
  const balance = calcBalanceCajaFisicaSesion(
    montoInicial,
    ventSnap,
    MAY12_SESION_NOCHE_ID,
    movSnap,
  );
  const montoEsperado = balance.montoEsperado;
  const montoCierre = montoEsperado;

  return {
    id: DEMO_CIERRE_NOCHE_ID,
    abiertaEn: "2026-05-12T19:15:00.000-05:00",
    cerradaEn: "2026-05-12T22:40:00.000-05:00",
    montoInicial,
    montoCierre,
    diferencia: 0,
    userId: SC.id,
    estado: "cerrada",
    openedByName: SC.nombre,
    closedByName: SC.nombre,
    label: "Turno noche",
    puntoCajaId: MAY12_PUNTO_ID,
    puntoCajaNombre: "Caja principal",
    sesionId: MAY12_SESION_NOCHE_ID,
    ventasCount: ventSnap.length,
    ventasSnapshot: ventSnap,
    movimientosSnapshot: movSnap,
    totalVentasEfectivo: balance.totalEfectivoVentas,
    totalVentasGeneral: resumen.total,
    montoEsperado,
    totalRetiros: 0,
    totalIngresos: 30_000,
    totalIngresosEfectivo: 30_000,
    totalIngresosDigital: 0,
  };
}

/** Los tres cierres de la jornada demo (mañana + tarde + noche). */
export function buildMay12JornadaCierres(): CierreCaja[] {
  return [
    buildMay12CierreCaja(),
    buildMay12CierreTarde(),
    buildMay12CierreNoche(),
  ];
}

/**
 * Sesión abierta en POS: mismo turno demo con todas las ventas y movimientos.
 * El ticket de cierre aparece en historial solo después de «Finalizar cierre» en caja.
 */
export const MAY12_DEMO_SESION_ABIERTA = {
  id: MAY12_SESION_MAIN_ID,
  fechaApertura: "2026-05-12T07:55:00.000-05:00",
  abiertaEn: "2026-05-12T07:55:00.000-05:00",
  montoInicial: 30_000,
  estado: "abierta",
  openedBy: JP.id,
  openedByName: JP.nombre,
  openedByEmail: "juan.perez@altenot.com",
  puntoCajaId: MAY12_PUNTO_ID,
};

/** @deprecated Usar `MAY12_DEMO_SESION_ABIERTA` */
export const MAY12_OPEN_SESION = MAY12_DEMO_SESION_ABIERTA;
